import { useEffect, useId, useRef, useState, type RefObject } from 'react'
import { PexIcon } from '../../../components/icons/PexIcon'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'

type HelpMenuLauncherProps = {
  canChat: boolean
  canOpenReports: boolean
  canCreateReport: boolean
  unreadCount: number
  onChat: () => void
  launcherRef?: RefObject<HTMLButtonElement | null>
}

export function HelpMenuLauncher({
  canChat,
  canOpenReports,
  canCreateReport,
  unreadCount,
  onChat,
  launcherRef: externalLauncherRef,
}: HelpMenuLauncherProps) {
  const t = useT(messagingI18n)
  const [open, setOpen] = useState(false)
  const id = useId()
  const internalLauncherRef = useRef<HTMLButtonElement>(null)
  const launcherRef = externalLauncherRef ?? internalLauncherRef
  const optionsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    optionsRef.current?.querySelector('button')?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      setOpen(false)
      launcherRef.current?.focus()
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return
      if (optionsRef.current?.contains(event.target) || launcherRef.current?.contains(event.target)) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open, launcherRef])

  if (!canChat && !canOpenReports) return null

  return (
    <div data-problem-report-overlay>
      {open && (
        <div
          ref={optionsRef}
          id={id}
          role="group"
          aria-label={t.launcher.helpOptions}
          className="fixed bottom-[calc(max(1rem,env(safe-area-inset-bottom))+3.75rem)] right-4 z-40 min-w-48 rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface)] p-1.5 text-[var(--color-text)] shadow-[var(--shadow-float)] sm:bottom-20 sm:right-6"
        >
          {canChat && (
            <button
              type="button"
              className="flex min-h-11 w-full items-center gap-2 rounded-[var(--radius-control)] px-3 text-left text-sm hover:bg-[var(--color-surface-muted)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
              onClick={() => { setOpen(false); onChat() }}
            >
              <PexIcon name="messages" size="sm" decorative />
              {t.launcher.chatChoice}
            </button>
          )}
          {canOpenReports && (
            <button
              type="button"
              className="flex min-h-11 w-full items-center gap-2 rounded-[var(--radius-control)] px-3 text-left text-sm hover:bg-[var(--color-surface-muted)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
              onClick={() => {
                setOpen(false)
                window.dispatchEvent(new CustomEvent('pexify:open-problem-report', {
                  detail: {
                    tab: canCreateReport ? 'new' : 'own',
                    returnFocus: launcherRef.current,
                  },
                }))
              }}
            >
              <PexIcon name="help" size="sm" decorative />
              {canCreateReport ? t.launcher.reportChoice : t.launcher.historyChoice}
            </button>
          )}
        </div>
      )}
      <button
        ref={launcherRef}
        type="button"
        aria-label={t.launcher.openHelp}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(current => !current)}
        className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-40 inline-flex min-h-12 min-w-12 items-center justify-center rounded-[var(--radius-panel)] bg-[var(--color-action)] text-[var(--color-action-foreground)] shadow-[var(--shadow-float)] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] sm:bottom-6 sm:right-6 sm:min-h-11 sm:min-w-11"
      >
        <PexIcon name="help" size="md" tone="on-emphasis" decorative />
        {unreadCount > 0 && (
          <span
            aria-label={t.launcher.unread(unreadCount)}
            className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full border-2 border-[var(--color-surface)] bg-[var(--color-signal)] px-1 text-[10px] font-bold leading-none text-white"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    </div>
  )
}
